/**
 * What a child's device asks for, and what it can read.
 *
 * Four routes, none of which takes a person parameter: the person id comes
 * from the token, so this app cannot name somebody else even in principle.
 * That is enforced in services/api/valley/handlers/child.py, not here -- this
 * module could not widen the query if it tried.
 *
 * Reasons are opened with the PERSON key and that person's own `rkey`, which
 * the server copies onto their share rows. A sibling's wrapping is on a row
 * this query cannot reach, and would not open with this key if it were.
 */

import { assertKnownOrigin, config } from '../config';
import type { AvatarSpec } from '../avatar/avatar';
import { parseFragment, sealable } from '../avatar/fragment';
import { openReason } from '../crypto/content';
import { setUnit } from '../family/money';
import { setFamilyScene } from '../valley/scene';
import { openUnit, type RawUnit } from '../family/state';
import { encryptField, decryptField } from '../crypto/familyKey';

export interface OwnEntry {
  id: string;
  date: string;
  /** This person's share, in integer cents. */
  amount: number;
  kind: string;
  voided: boolean;
  /** Decrypted with this person's own key. Empty if it predates the scheme. */
  reason: string;
  /** What automatic splits set aside from it: goal id -> cents. */
  saved?: Record<string, number>;
}

export interface OwnGoal {
  id: string;
  /** Decrypted here with this child's own title key. Empty if the goal
   *  predates them having one -- the copy says to ask a grown-up. */
  title: string;
  target: number;
  icon: string;
  status: string;
  /** How much of THIS person's money counts toward it. Never the family pot. */
  mine: number;
  /** Everything in the goal, from everybody on it. */
  pot: number;
  /** A grown-up locked it: money can go in, not come back out. */
  locked: boolean;
}

export interface OwnState {
  name: string;
  /** Their character, as last approved. Null if they have never had one. */
  avatar: AvatarSpec | null;
  /** One they designed that is waiting for a grown-up. */
  pendingAvatar: AvatarSpec | null;
  balance: number;
  /** Of `balance`, how much is set aside in goals. */
  setAside: number;
  /** Which family this is, for a child who belongs to more than one. */
  familyName: string;
  /** Their own allowance, if they have one. */
  allowance: { amount: number; every: 'week' | 'fortnight' | 'month'; next: string; paused: boolean } | null;
  goals: OwnGoal[];
  entries: OwnEntry[];
  today: string;
  revision: number;
}

/**
 * A failed request, carrying its status.
 *
 * The status is the whole reason this exists. A 403 from a child route means
 * one specific thing -- a parent unlinked this device -- and the page has to
 * say so and stop, rather than showing "something went wrong" and retrying
 * into a wall it can never get past.
 */
export class ChildApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = 'ChildApiError';
  }
}

async function get<T>(path: string, token: string): Promise<T> {
  const url = `${config.apiBase}${path}`;
  assertKnownOrigin(url);

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
  });
  const payload = (await response.json().catch(() => ({}))) as T & { error?: string };
  if (!response.ok) {
    throw new ChildApiError(
      payload.error ?? 'Something went wrong. Ask a grown-up.',
      response.status,
    );
  }
  return payload;
}

/** One small read while the page is open; a changed number triggers loadOwn. */
export async function readOwnRevision(token: string): Promise<number> {
  const result = await get<{ revision: number }>('/v1/child/revision', token);
  if (!Number.isSafeInteger(result.revision) || result.revision < 0) {
    throw new Error('The valley could not check for updates.');
  }
  return result.revision;
}

async function readOwn(key: CryptoKey | null, sealed?: string | null): Promise<string> {
  if (!key || !sealed) return '';
  try {
    return await decryptField(key, sealed);
  } catch {
    return '';
  }
}

export async function loadOwn(
  token: string,
  key: CryptoKey | null,
): Promise<OwnState> {
  const [state, journal] = await Promise.all([
    get<{
      person: { pf?: string | null; pendCt?: string | null };
      balance: number;
      setAside?: number;
      allowance?: OwnState['allowance'];
      unit?: RawUnit;
      scene?: string | null;
      family?: { nameCt?: string | null; fkey?: string | null };
      goals: { id: string; titleCt?: string | null; tkey?: string | null;
               target: number; icon: string; status: string; mine: number; pot?: number; locked?: boolean }[];
      today: string;
      revision: number;
    }>('/v1/child/state', token),
    get<{
      transactions: { id: string; date: string; share: number; kind: string;
                      voided?: boolean; reasonCt?: string | null;
                      rkey?: string | null; saved?: Record<string, number> }[];
    }>('/v1/child/journal', token),
  ]);

  // What the family counts in, read with this child's own wrapping.
  setUnit(await openUnit(state.unit, key, state.unit?.ukey));
  // The family's valley background, so this device paints the same valley.
  setFamilyScene(state.scene);

  const [own, goals, entries, draft] = await Promise.all([
    // A child's own name and character are sealed under their own key,
    // which is the whole reason a person key exists rather than the family
    // key doing everything.
    readOwn(key, state.person.pf).then(parseFragment),
    Promise.all(
      state.goals.map(async (goal) => ({
        id: goal.id,
        // `tkey` is THIS child's wrapping of the goal's title key, copied out
        // by the server the same way `rkey` is copied onto a share row. The
        // same call opens a reason, because it is the same shape.
        //
        // A goal saved before title keys existed has no `tkey`, and its title
        // is sealed under the family key -- which this device does not have
        // and should not. That comes back empty rather than throwing, and the
        // screen says to ask a grown-up to open the goal once.
        title: await openReason(key, goal.titleCt, goal.tkey),
        target: goal.target,
        icon: goal.icon,
        status: goal.status,
        mine: goal.mine,
        // Everything in it, from everybody saving for it: the same bar for
        // every child on the goal. Older servers sent only `mine`.
        pot: goal.pot ?? goal.mine,
        locked: goal.locked ?? true,
      })),
    ),
    Promise.all(
      journal.transactions.map(async (entry) => ({
        id: entry.id,
        date: entry.date,
        amount: entry.share,
        kind: entry.kind,
        voided: Boolean(entry.voided),
        // An allowance payment has no reason; nobody typed one.
        reason: (await openReason(key, entry.reasonCt, entry.rkey)) || (entry.kind === 'allowance' ? 'Allowance' : ''),
        ...(entry.saved ? { saved: entry.saved } : {}),
      })),
    ),
    readOwn(key, state.person.pendCt).then(parseFragment),
  ]);

  return {
    name: own?.name ?? '',
    avatar: own?.avatar ?? null,
    pendingAvatar: draft?.avatar ?? null,
    balance: state.balance,
    setAside: state.setAside ?? 0,
    familyName: await openReason(key, state.family?.nameCt, state.family?.fkey),
    allowance: state.allowance ?? null,
    goals,
    entries,
    today: state.today,
    revision: state.revision,
  };
}


/**
 * Send a character to a grown-up for approval.
 *
 * The only write a child's device can make, and it cannot change anything a
 * family sees: the server queues it beside the current character until a
 * parent says yes. That is the display's rule for its builder, and why the
 * builder needs no password.
 *
 * WHAT IS SENT: one sealed fragment -- this child's name and the new
 * character -- under the child's OWN key, so their parent's device can open
 * it and the server cannot. What is deliberately NOT sent is `pfw`: that is
 * the key a parent uses to open this child at all, and a draft has no
 * business touching it (the server refuses it now too).
 */
export async function proposeLook(
  token: string,
  key: CryptoKey | null,
  name: string,
  avatar: AvatarSpec,
): Promise<void> {
  if (!key) {
    throw new ChildApiError(
      'This device cannot save a character yet. Ask a grown-up to link it again.',
      409,
    );
  }
  const url = `${config.apiBase}/v1/child/draft`;
  assertKnownOrigin(url);
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify({
      pf: await encryptField(key, sealable({ name, avatar })),
      pfv: 1,
    }),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ChildApiError(payload.error ?? 'That did not send. Try again.', response.status);
  }
}


/**
 * Move some of this child's own money into a goal they are on, or back out
 * of one to spend or to move again.
 *
 * The one child write that moves money, and the server holds it to exactly
 * that: the person comes from the token; in, never more than they have free
 * (a child can never take themselves below zero); out, never more than they
 * put in, and never from a goal a grown-up has locked.
 */
export async function childMove(
  token: string,
  goalId: string,
  amount: string,
  direction: 'in' | 'out' = 'in',
): Promise<void> {
  const url = `${config.apiBase}/v1/child/goals/${encodeURIComponent(goalId)}/funds`;
  assertKnownOrigin(url);
  const response = await fetch(url, {
    method: 'POST',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    body: JSON.stringify({ amount, direction }),
  });
  if (!response.ok) {
    const payload = (await response.json().catch(() => ({}))) as { error?: string };
    throw new ChildApiError(payload.error ?? 'That did not work. Try again.', response.status);
  }
}
